import React, { useState, useEffect } from "react";
import type { Customer } from "../../types/store";

interface CustomerSelectionModalProps {
  customers: Customer[];
  onSelect: (customer: Customer | null) => void;
  onAddNew: (newCustomer: Partial<Customer>) => void;
  onClose: () => void;
}

const CustomerSelectionModal: React.FC<CustomerSelectionModalProps> = ({
  customers,
  onSelect,
  onAddNew,
  onClose,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>(customers);
  const [newCustomer, setNewCustomer] = useState({ firstname: "", lastname: "", othername: "", email: "", phone: "" });
  const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);

  useEffect(() => {
    const results = customers.filter((c) =>
      c.firstname.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.lastname.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.othername?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredCustomers(results);
  }, [searchTerm, customers]);

  const handleAddCustomer = () => {
    if (!newCustomer.firstname || !newCustomer.lastname || !newCustomer.email) return;
    onAddNew(newCustomer);
    setNewCustomer({ firstname: "", lastname: "", othername: "", email: "", phone: "" });
    setShowNewCustomerForm(false);
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
        <h2 className="text-xl font-semibold mb-4">Select Customer</h2>

        {/* Search Field */}
        <input
          type="text"
          placeholder="Search by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="border rounded-md w-full p-2 mb-3"
        />

        {/* Customer List */}
        {!showNewCustomerForm && (
          <div className="max-h-48 text-left overflow-y-auto border rounded-md">
            {filteredCustomers.length > 0 ? (
              filteredCustomers.map((c) => (
                <div
                  key={c.id}
                  onClick={() => onSelect(c)}
                  className="p-3 hover:bg-blue-100 cursor-pointer border-b text-sm"
                >
                  <p className="font-medium">{c.firstname} {c.othername} {c.lastname}</p>
                  <p className="text-gray-500">{c.email}</p>
                </div>
              ))
            ) : (
              <p className="p-3 text-gray-400 text-sm">No customers found</p>
            )}
          </div>
        )}

        {/* Add New Customer Button */}
        {!showNewCustomerForm && (
          <button
            onClick={() => setShowNewCustomerForm(true)}
            className="mt-4 text-blue-600 hover:underline text-sm"
          >
            + Add New Customer
          </button>
        )}

        {/* New Customer Form */}
        {showNewCustomerForm && (
          <div className="mt-4 space-y-2">
            <input
              type="text"
              placeholder="First Name"
              value={newCustomer.firstname}
              onChange={(e) => setNewCustomer({ ...newCustomer, firstname: e.target.value })}
              className="border rounded-md w-full p-2"
            />
            <input
              type="text"
              placeholder="Other Name(s)"
              value={newCustomer.othername}
              onChange={(e) => setNewCustomer({ ...newCustomer, othername: e.target.value })}
              className="border rounded-md w-full p-2"
            />
            <input
              type="text"
              placeholder="Last Name"
              value={newCustomer.lastname}
              onChange={(e) => setNewCustomer({ ...newCustomer, lastname: e.target.value })}
              className="border rounded-md w-full p-2"
            />
            <input
              type="email"
              placeholder="Email"
              value={newCustomer.email}
              onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
              className="border rounded-md w-full p-2"
            />
            <input
              type="text"
              placeholder="Phone (optional)"
              value={newCustomer.phone}
              onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
              className="border rounded-md w-full p-2"
            />
            <div className="flex justify-end gap-3 mt-3">
              <button
                onClick={handleAddCustomer}
                className="bg-primary text-white py-2 px-4 rounded-md hover:bg-blue-900"
              >
                Save Customer
              </button>
              <button
                onClick={() => setShowNewCustomerForm(false)}
                className="text-gray-600 hover:underline"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <button
          onClick={onClose}
          className="mt-6 bg-red-300 text-red-800 py-2 px-4 rounded-md hover:bg-red-400 w-full"
        >
          Close
        </button>
      </div>
    </div>
  );
};

export default CustomerSelectionModal;
